/**
 * CartSubscriptionUpsell
 *
 * Custom element that handles one-click switching of a cart line item
 * from a one-time purchase to a subscription (selling plan).
 *
 * Usage in Liquid:
 *   <cart-subscription-upsell data-line="{{ item.index | plus: 1 }}" data-selling-plan="{{ selling_plan_id }}">
 *     <button type="button">Subscribe</button>
 *   </cart-subscription-upsell>
 */
class CartSubscriptionUpsell extends HTMLElement {
  connectedCallback() {
    this.querySelector('button')?.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.querySelector('button')?.removeEventListener('click', this.#handleClick);
  }

  #handleClick = async () => {
    const line = parseInt(this.dataset.line, 10);
    const sellingPlan = parseInt(this.dataset.sellingPlan, 10);
    const button = this.querySelector('button');

    if (!line || !sellingPlan || !button) return;

    button.disabled = true;
    button.classList.add('loading');

    try {
      const changeRes = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, selling_plan: sellingPlan }),
      });

      if (!changeRes.ok) {
        button.disabled = false;
        button.classList.remove('loading');
        return;
      }

      // Re-render the cart drawer using section rendering
      const cartItemsEl = this.closest('cart-items-component');
      const sectionId = cartItemsEl?.dataset.sectionId;

      if (sectionId) {
        const sectionsRes = await fetch(`/?sections=${sectionId}`);
        const sections = await sectionsRes.json();
        const html = sections[sectionId];

        if (html) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const selector = cartItemsEl.hasAttribute('data-drawer')
            ? 'cart-items-component[data-drawer]'
            : 'cart-items-component:not([data-drawer])';
          const newEl = doc.querySelector(selector);

          if (newEl && cartItemsEl) {
            cartItemsEl.innerHTML = newEl.innerHTML;
            // Re-upgrade any custom elements in the new HTML
            document.querySelectorAll('cart-subscription-upsell').forEach((el) => {
              if (!el.isConnected) return;
              const btn = el.querySelector('button');
              btn?.addEventListener('click', el.#handleClick);
            });
          }
        }
      } else {
        // Fallback: reload the page
        window.location.reload();
      }
    } catch {
      button.disabled = false;
      button.classList.remove('loading');
    }
  };
}

if (!customElements.get('cart-subscription-upsell')) {
  customElements.define('cart-subscription-upsell', CartSubscriptionUpsell);
}
